/**
 * BioChat frontend observability.
 *
 * Emits compact events as OpenTelemetry spans (Grafana Cloud Tempo via the
 * existing /api/telemetry/traces proxy). High-volume UI chatter is sampled
 * unless REACT_APP_OMICS_OBSERVABILITY_VERBOSE=1.
 */

import { recordOmicsEvent } from '../../../telemetry';

/** Events that fire too often for default Tempo sampling. */
const HIGH_VOLUME_MESSAGES = new Set([
  'fe_composer_type',
  'fe_chatpane_render',
  'fe_containers_inventory',
  'fe_stream_event',
  'fe_stream_delta',
]);

function observabilityVerbose() {
  const raw = (process.env.REACT_APP_OMICS_OBSERVABILITY_VERBOSE || '').trim().toLowerCase();
  return raw === '1' || raw === 'true' || raw === 'yes';
}

/**
 * Record a BioChat UI event for Grafana Tempo.
 * API kept stable so existing call sites stay unchanged.
 */
export function emitDebugSessionLog({
  location,
  message,
  hypothesisId,
  data = {},
  runId,
} = {}) {
  const eventName = message || 'omics_event';
  if (HIGH_VOLUME_MESSAGES.has(eventName) && !observabilityVerbose()) {
    return;
  }
  recordOmicsEvent(eventName, {
    location: location || 'unknown',
    hypothesis_id: hypothesisId || '',
    run_id: runId || '',
    layer: 'frontend',
    ...(data && typeof data === 'object' ? data : {}),
  });
}

function resolveToolName(event = {}) {
  const fromTools = Array.isArray(event.tools) ? event.tools[0] : null;
  const fromDetails = Array.isArray(event.tool_details)
    ? (event.tool_details[0]?.name || event.tool_details[0]?.tool_name)
    : null;
  return (
    event.tool_name
    || event.name
    || event.toolName
    || fromTools
    || fromDetails
    || null
  );
}

export function summarizeStreamEvent(event = {}) {
  const type = event.type || event.__event__ || '';
  const out = {
    type,
    turn_seq: event.turn_seq ?? null,
    tool_name: resolveToolName(event),
    tool_use_id: event.tool_use_id || event.toolUseId || event.id || null,
    chat_id: event.chat_id || event.chatId || event.omics_chat_id || null,
    project_id: event.project_id || event.projectId || null,
    node_id: event.node_id || event.nodeId || null,
  };
  if (type === 'bedrock_request' || event.estimated_input_tokens != null) {
    out.estimated_input_tokens = event.estimated_input_tokens ?? null;
    out.message_count = event.message_count ?? null;
    out.context_summarized = event.context_summarized ?? null;
    out.round = event.round ?? null;
  }
  if (type === 'tool_result' || type === 'tool_call_result') {
    out.is_error = Boolean(event.is_error);
    out.result_chars = JSON.stringify(event.results || event.result || '').length;
  }
  if (
    type === 'session_execute_start'
    || type === 'session_execute_finished'
    || type === 'session_execute_progress'
  ) {
    out.exec_id = event.exec_id || event.execId || null;
    out.session_id = event.session_id || event.sessionId || null;
    out.status = event.status || null;
    out.is_error = Boolean(event.is_error);
  }
  if (type === 'done') {
    out.message_chars = String(event.message || '').length;
    out.tool_usage_counts = event.tool_usage_counts || {};
  }
  return out;
}
